/**
 * CreatorApp
 *
 * Top-level shell for the Creator domain (mounted at ``/creator/:clientId``
 * and ``/creator/:clientId/briefs/:briefId``). Mirrors the shape of
 * ``editorRoutes`` — an independent app tree, NOT nested inside the
 * client-approval ``ApprovalView``. Today it serves on-site
 * photographers; the video editor role will land here next.
 *
 * Responsibilities:
 * - Own auth gate: validates the portal JWT stored under
 *   ``touchpoint_portal_token_<clientId>``. Unauthenticated visitors see
 *   an inline "session expired" panel prompting a new magic link.
 * - Mount the portal ``HttpClient`` so Ideas/Briefs hooks issue portal-
 *   scoped requests.
 * - Header mirrors the admin arrangement: burger + Brief/Idee TabGroup
 *   on the left, workspace switcher on the right (DD-011 transparent
 *   header). Logout lives at the bottom of the full-screen burger menu.
 */

import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams, Navigate } from "react-router-dom";
import { HttpClientProvider } from "@/core/http/HttpClientContext";
import { createPortalClient } from "@/services/portalClient";
import type { HttpClient } from "@/core/http/types";
import { clearPortalAuth } from "@/core/auth/storage";
import { usePortalAuth } from "@/core/auth/hooks/usePortalAuth";
import { RoleProvider } from "@/contexts/RoleContext";
import { UserRole } from "@/services/urlService";
import { useLocale } from "@/hooks/useLocale";
import { SpinnerIcon, MenuIcon, CloseIcon } from "@/components/icons";
import { TabGroup, TabItem } from "@/components/ui/TabGroup";
import { TrailDisplayProvider } from "@/core/navigation/TrailDisplayContext";
import { BreadcrumbTrail } from "@/components/Header/BreadcrumbTrail";
import { CreatorBriefsListTab } from "./components/CreatorBriefsListTab";
import { CreatorIdeasTab } from "./components/CreatorIdeasTab";
import { CreatorBriefDetail } from "./components/CreatorBriefDetail";
import { CreatorClientSwitcher } from "./components/CreatorClientSwitcher";

type TabId = "brief" | "ideas";

/**
 * Inline error panel shown when no valid portal JWT is present. Italian
 * copy to match the rest of the portal-side error screens.
 */
const CreatorSessionExpired: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const handleRequestNewLink = () => {
    // Redirect to the canonical expired screen which already explains how
    // to request a fresh magic link.
    navigate(`/client/${clientId}/expired`, { replace: true });
  };
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {t("creator.session.expiredTitle")}
        </h1>
        <p className="text-gray-600 mb-6">
          {t("creator.session.expiredDescription")}
        </p>
        <button
          type="button"
          onClick={handleRequestNewLink}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          OK
        </button>
      </div>
    </div>
  );
};

interface CreatorMenuProps {
  onClose: () => void;
  onLogout: () => void;
}

/** Full-screen burger menu. Navigation lives in the header tab track —
 * this holds only the logout, pinned at the bottom. */
const CreatorMenu: React.FC<CreatorMenuProps> = ({ onClose, onLogout }) => {
  const { t } = useLocale();
  return (
    <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col">
      <div className="max-w-4xl mx-auto w-full px-4 lg:px-6 py-3 flex items-center justify-end">
        <button
          type="button"
          onClick={onClose}
          aria-label={t("creator.menu.close")}
          className="p-2 -mr-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <CloseIcon className="w-6 h-6" />
        </button>
      </div>
      <div className="flex-1" />
      <div className="max-w-4xl mx-auto w-full px-4 lg:px-6 pb-8">
        <button
          type="button"
          onClick={onLogout}
          className="w-full text-left px-3 py-3 rounded-lg text-lg font-semibold text-red-600 hover:bg-red-50 transition-colors"
        >
          {t("creator.header.logout")}
        </button>
      </div>
    </div>
  );
};

interface CreatorHeaderProps {
  clientId: string;
  clientName: string | undefined;
  activeTab: TabId;
  onTabChange: (id: TabId) => void;
  onOpenMenu: () => void;
}

const CreatorHeader: React.FC<CreatorHeaderProps> = ({
  clientId,
  clientName,
  activeTab,
  onTabChange,
  onOpenMenu,
}) => {
  const { t } = useLocale();
  const items: TabItem<TabId>[] = [
    { id: "brief", label: t("creator.tabs.brief") },
    { id: "ideas", label: t("creator.tabs.ideas") },
  ];
  return (
    // Transparent app header, no divider (DD-011). Burger + tab track on
    // the left, workspace switcher on the right — the admin arrangement.
    // Sticky so the trail stays reachable while scrolling on mobile.
    <header className="sticky top-0 z-40 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={onOpenMenu}
            aria-label={t("creator.menu.open")}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
          >
            <MenuIcon className="w-5 h-5" />
          </button>
          <TabGroup<TabId>
            items={items}
            activeId={activeTab}
            onTabChange={onTabChange}
            config={{
              variant: "navigation",
              size: "sm",
              layout: "horizontal",
              hasBackground: true,
            }}
          />
        </div>
        <div className="shrink-0 min-w-0">
          <CreatorClientSwitcher
            activeClientId={clientId}
            activeClientName={clientName ?? null}
          />
        </div>
      </div>
      {/* Drill-down trail under the tab row, same as the admin header. */}
      <div className="max-w-4xl mx-auto px-4 lg:px-6">
        <BreadcrumbTrail />
      </div>
    </header>
  );
};

/**
 * Root component for the ``/creator/:clientId`` routes.
 */
export const CreatorApp: React.FC = () => {
  const { clientId, briefId } = useParams<{ clientId: string; briefId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const legacyBriefId = searchParams.get("briefId");
  // The active tab rides in ?tab= so reload, back and shared links keep
  // the creator where they were.
  const activeTab: TabId = searchParams.get("tab") === "ideas" ? "ideas" : "brief";
  const [ideasVisited, setIdeasVisited] = useState(activeTab === "ideas");
  const [menuOpen, setMenuOpen] = useState(false);

  React.useEffect(() => {
    if (activeTab === "ideas") setIdeasVisited(true);
  }, [activeTab]);
  const {
    isAuthenticated,
    isCheckingSession,
    clientName,
  } = usePortalAuth({ clientId });

  const portalHttp = React.useMemo(
    () => (clientId ? (createPortalClient(clientId, "client") as unknown as HttpClient) : null),
    [clientId]
  );

  const handleLogout = () => {
    if (!clientId) return;
    clearPortalAuth(clientId);
    // Deliberate logout lands on the unified login (DD-026), not the
    // expired-link error screen.
    window.location.assign("/portal/login");
  };

  const handleTabChange = (id: TabId) => {
    if (id === "ideas") setIdeasVisited(true);
    if (briefId && clientId) {
      // Tab taps from the brief detail page return to the list shell.
      navigate(`/creator/${clientId}${id === "ideas" ? "?tab=ideas" : ""}`);
      return;
    }
    setSearchParams(id === "ideas" ? { tab: "ideas" } : {});
  };

  if (!clientId) return null;

  // Legacy magic-link deep links: /creator/:clientId?briefId=X
  if (!briefId && legacyBriefId) {
    return (
      <Navigate to={`/creator/${clientId}/briefs/${legacyBriefId}`} replace />
    );
  }

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <SpinnerIcon className="w-6 h-6 text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated || !portalHttp) {
    return <CreatorSessionExpired clientId={clientId} />;
  }

  return (
    <HttpClientProvider client={portalHttp}>
      <RoleProvider role={UserRole.CLIENT} clientId={clientId}>
        <TrailDisplayProvider>
        <div className="min-h-screen bg-gray-50">
          <CreatorHeader
            clientId={clientId}
            clientName={clientName || undefined}
            activeTab={briefId ? "brief" : activeTab}
            onTabChange={handleTabChange}
            onOpenMenu={() => setMenuOpen(true)}
          />
          <main>
            {briefId ? (
              <div className="max-w-4xl mx-auto px-4 lg:px-6 py-5">
                <CreatorBriefDetail clientId={clientId} briefId={briefId} />
              </div>
            ) : (
              <div className="max-w-4xl mx-auto px-4 lg:px-6 py-5">
                <div className={activeTab === "brief" ? "block" : "hidden"}>
                  <CreatorBriefsListTab clientId={clientId} />
                </div>
                <div className={activeTab === "ideas" ? "block" : "hidden"}>
                  {ideasVisited ? <CreatorIdeasTab clientId={clientId} /> : null}
                </div>
              </div>
            )}
          </main>
          {menuOpen && (
            <CreatorMenu
              onClose={() => setMenuOpen(false)}
              onLogout={handleLogout}
            />
          )}
        </div>
        </TrailDisplayProvider>
      </RoleProvider>
    </HttpClientProvider>
  );
};

export default CreatorApp;
